import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db, isSupabaseConfigured } from '../lib/supabase';
import { logger } from '../lib/logger';
import { CACHE_CONFIG } from '../lib/constants';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [linkedProviders, setLinkedProviders] = useState([]);
  const currentUserIdRef = useRef(null);

  // Helper to clear all auth state
  const clearAuthState = () => {
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setLinkedProviders([]);
    currentUserIdRef.current = null;
  };

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    // Check current session
    const initAuth = async () => {
      try {
        const { data: { session } } = await auth.getSession();
        if (session?.user) {
          setUser(session.user);
          currentUserIdRef.current = session.user.id;
          await fetchProfile(session.user.id);
        } else {
          // No valid session - ensure state is clear
          clearAuthState();
        }
      } catch (error) {
        logger.error('Auth init error:', error);
        // On error, clear state to be safe
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      logger.log('Auth event:', event, session?.user?.id);

      if (session?.user) {
        // Handle SIGNED_IN and INITIAL_SESSION - fetch profile
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          setUser(session.user);
          currentUserIdRef.current = session.user.id;
          // Don't await - let profile load in background so login isn't blocked
          fetchProfile(session.user.id);
        } else if (event === 'TOKEN_REFRESHED') {
          // Token refreshed - just update user, profile stays the same
          setUser(session.user);
        }
      } else {
        // No session - clear state (handles SIGNED_OUT and other cases)
        clearAuthState();
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Profile fetch timeout')), CACHE_CONFIG.profileFetchTimeout)
    );

    try {
      console.log('[DEBUG] Fetching profile for:', userId);
      const fetchPromise = db.profiles.get(userId);
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      console.log('[DEBUG] Profile fetch result:', { data: data ? 'found' : 'null', error: error?.message || 'none' });

      if (error) {
        logger.error('Error fetching profile:', error);
        console.error('[DEBUG] Profile fetch error:', error);
        setProfile(null);
        setIsAdmin(false);
        return;
      }
      if (!data) {
        logger.warn('No profile found for user:', userId);
        console.warn('[DEBUG] No profile data for user:', userId);
        setProfile(null);
        setIsAdmin(false);
        return;
      }

      // SECURITY FIX: Check if user account is suspended
      if (data.is_suspended) {
        logger.warn('User account is suspended:', userId);
        setProfile(null);
        setIsAdmin(false);
        // Sign out the suspended user
        await auth.signOut();
        return;
      }

      console.log('[DEBUG] Profile loaded successfully, role:', data.role);
      logger.log('Profile loaded');
      setProfile(data);
      setIsAdmin(data?.role === 'admin' || data?.role === 'super_admin');

      // Fetch linked service providers for the user
      fetchLinkedProviders(userId);
    } catch (error) {
      logger.error('Error fetching profile:', error.message);
      console.error('[DEBUG] Profile fetch exception:', error.message);
      setProfile(null);
      setIsAdmin(false);
    }
  };

  // Fetch service providers linked to this user (via user_id)
  const fetchLinkedProviders = async (userId) => {
    try {
      const { data, error } = await db.serviceProviders.getByOwnerId(userId);
      if (error) {
        logger.error('Error fetching linked providers:', error);
        setLinkedProviders([]);
        return;
      }
      if (data && data.length > 0) {
        logger.log('Linked providers found:', data.length);
        setLinkedProviders(data);
      } else {
        setLinkedProviders([]);
      }
    } catch (error) {
      logger.error('Error fetching linked providers:', error.message);
      setLinkedProviders([]);
    }
  };

  const signUp = async (email, password, fullName, userType = 'parent') => {
    try {
      const { data, error } = await auth.signUp(email, password, {
        full_name: fullName,
        user_type: userType
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await auth.signIn(email, password);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    // Always clear local state, regardless of Supabase response
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    try {
      await auth.signOut();
      return { error: null };
    } catch (error) {
      // State is already cleared, just return the error
      return { error };
    }
  };

  const deleteAccount = async () => {
    if (!user) return { error: { message: 'Not authenticated' } };
    try {
      // Delete profile data first
      const { error: profileError } = await db.profiles.delete(user.id);
      if (profileError) console.warn('Profile deletion warning:', profileError);

      // Clear all local storage data
      localStorage.clear();

      // Sign out the user
      await auth.signOut();

      // Clear local state
      setUser(null);
      setProfile(null);
      setIsAdmin(false);

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'Not authenticated' } };
    try {
      const { data, error } = await db.profiles.update(user.id, updates);
      if (error) throw error;
      setProfile(data);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    profile,
    loading,
    isAdmin,
    linkedProviders,
    // Check if user has any linked business
    hasLinkedBusiness: linkedProviders.length > 0,
    // User is authenticated if we have a user object
    isAuthenticated: !!user,
    // Profile is still loading if user exists but profile doesn't
    profileLoading: !!user && !profile,
    isSupabaseConfigured: isSupabaseConfigured(),
    signUp,
    signIn,
    signOut,
    deleteAccount,
    updateProfile,
    refreshProfile: () => user && fetchProfile(user.id),
    refreshLinkedProviders: () => user && fetchLinkedProviders(user.id)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
